import { LitElement, html, css, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import { styles as sharedStyles } from '../styles/shared-styles';
import { resolveRouterPath, router } from '../router';
import { HELP_GROUPS } from './help-manifest';
import {
  allHelpSlugs,
  helpTopicExists,
  helpTopicTitle,
  helpTopicSummary,
} from './help-content';

// The manual's table of contents at /help. Groups + ordering come from the
// manifest; titles + blurbs are derived from each topic's markdown. Any content
// file missing from the manifest is surfaced under "Other" rather than silently
// dropped.
@customElement('help-index-page')
export class HelpIndexPage extends LitElement {
  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
      }
      .wrap {
        max-width: 760px;
        margin: 0 auto;
        padding: clamp(1.25rem, 4vw, 2.5rem) clamp(1rem, 4vw, 2rem) 3rem;
        box-sizing: border-box;
      }
      .page-head {
        margin-bottom: 1.5rem;
      }
      .page-head h1 {
        font-family: var(--font-display, inherit);
        font-size: 1.6rem;
        font-weight: 800;
        letter-spacing: -0.02em;
        margin: 0 0 0.35rem;
      }
      .page-head p {
        margin: 0;
        color: var(--sl-color-neutral-600);
      }
      .group {
        margin-top: 1.75rem;
      }
      .group-label {
        text-transform: uppercase;
        letter-spacing: 0.06em;
        font-size: 0.72rem;
        font-weight: 700;
        color: var(--sl-color-neutral-500);
        margin: 0 0 0.6rem;
      }
      .topic {
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;
        padding: 0.85rem 0.9rem;
        border: 1px solid var(--sl-color-neutral-200);
        border-radius: 10px;
        margin-bottom: 0.5rem;
        text-decoration: none;
        color: inherit;
        background: var(--sl-color-neutral-0);
        transition:
          border-color 0.12s,
          background 0.12s;
      }
      .topic:hover {
        border-color: var(--sl-color-primary-400);
        background: var(--sl-color-primary-50);
      }
      .topic sl-icon {
        font-size: 1.1rem;
        color: var(--sl-color-primary-500);
        margin-top: 0.15rem;
        flex: none;
      }
      .topic .body {
        min-width: 0;
      }
      .topic .title {
        font-weight: 650;
        margin-bottom: 0.1rem;
      }
      .topic .summary {
        font-size: 0.88rem;
        color: var(--sl-color-neutral-600);
        line-height: 1.4;
      }
    `,
  ];

  private _go(e: Event, slug: string) {
    e.preventDefault();
    router.navigate(resolveRouterPath('help/' + slug));
  }

  private _topicRow(slug: string) {
    const title = helpTopicTitle(slug);
    const summary = helpTopicSummary(slug);
    return html`
      <a
        class="topic"
        href=${resolveRouterPath('help/' + slug)}
        @click=${(e: Event) => this._go(e, slug)}
      >
        <sl-icon name="file-earmark-text"></sl-icon>
        <span class="body">
          <span class="title">${title}</span>
          ${summary ? html`<span class="summary">${summary}</span>` : nothing}
        </span>
      </a>
    `;
  }

  render() {
    const inManifest = new Set(HELP_GROUPS.flatMap((g) => g.slugs));
    const orphans = allHelpSlugs().filter((s) => !inManifest.has(s));
    return html`
      <div class="wrap">
        <div class="page-head">
          <h1>Help &amp; training</h1>
          <p>
            Step-by-step guides for running the till, the trading day, stock,
            and the menu.
          </p>
        </div>
        ${HELP_GROUPS.map(
          (g) => html`
            <section class="group">
              <p class="group-label">${g.label}</p>
              ${g.slugs.filter((s) => helpTopicExists(s)).map((s) => this._topicRow(s))}
            </section>
          `
        )}
        ${
          orphans.length
            ? html`
                <section class="group">
                  <p class="group-label">Other</p>
                  ${orphans.map((s) => this._topicRow(s))}
                </section>
              `
            : nothing
        }
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'help-index-page': HelpIndexPage;
  }
}
